import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { format, addYears } from 'date-fns';

const AppContext = createContext(null);
const fmt = d => format(d, 'yyyy-MM-dd');
const API = '';

async function api(method, path, body) {
  const token = localStorage.getItem('nebb_token');
  const res = await fetch(`${API}/api${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    // Token vencido o inválido: cerramos sesión limpiamente
    localStorage.removeItem('nebb_user');
    localStorage.removeItem('nebb_token');
    window.location.href = '/login';
    throw new Error('Sesión expirada');
  }
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export function AppProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nebb_user')); } catch { return null; }
  });
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  }, []);

  // AUTH
  const login = async (email, password) => {
    try {
      const user = await api('POST', '/login', { email, password });
      const { token, ...safeUser } = user;
      if (token) localStorage.setItem('nebb_token', token);
      setCurrentUser(safeUser);
      localStorage.setItem('nebb_user', JSON.stringify(safeUser));
      return true;
    } catch { return false; }
  };
  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('nebb_user');
    localStorage.removeItem('nebb_token');
    setClients([]); setPolicies([]); setCompanies([]); setUsers([]);
  };

  // CLIENTS
  const addClient = async (data) => {
    const c = await api('POST', '/clients', data);
    setClients(prev => [...prev, c]);
    showToast('Cliente creado correctamente');
    return c;
  };
  const updateClient = async (id, data) => {
    const c = await api('PUT', `/clients/${id}`, data);
    setClients(prev => prev.map(x => x.id === id ? c : x));
    showToast('Cliente actualizado');
  };
  const deleteClient = async (id) => {
    await api('DELETE', `/clients/${id}`);
    setClients(prev => prev.filter(c => c.id !== id));
    setPolicies(prev => prev.filter(p => p.clientId !== id));
    showToast('Cliente eliminado', 'warning');
  };

  // POLICIES
  const addPolicy = async (data) => {
    const p = await api('POST', '/policies', { ...data, status: 'active' });
    setPolicies(prev => [...prev, p]);
    showToast('Póliza creada correctamente');
    return p;
  };
  const updatePolicy = async (id, data) => {
    const p = await api('PUT', `/policies/${id}`, data);
    setPolicies(prev => prev.map(x => x.id === id ? p : x));
    showToast('Póliza actualizada');
  };
  const endorsePolicy = async (id, endorsement) => {
    const policy = policies.find(p => p.id === id);
    const updated = {
      ...policy,
      ...endorsement.policyUpdates,
      status: 'endorsed',
      endorsements: [...(policy.endorsements || []), { ...endorsement, date: fmt(new Date()), id: 'end' + Date.now() }],
      history: [...(policy.history || []), { type: 'endoso', date: fmt(new Date()), desc: endorsement.description }],
    };
    const p = await api('PUT', `/policies/${id}`, updated);
    setPolicies(prev => prev.map(x => x.id === id ? p : x));
    showToast('Endoso registrado');
  };
  const renewPolicy = async (id, opts = {}) => {
    const policy = policies.find(p => p.id === id);
    // La renovación continúa desde donde termina la vigencia actual (endDate),
    // sin huecos. Las pólizas que entraron por un aviso de renovación ya tienen
    // cargada su vigencia nueva correcta (desde la fecha del aviso), así que al
    // renovar simplemente continúan ese ciclo.
    const base = new Date(policy.endDate || new Date());
    const newStart = fmt(base);
    const newEnd = fmt(addYears(base, 1));
    // Cambio de compañía opcional en la renovación.
    const nuevaCompania = (opts.company || '').trim();
    const cambioCompania = nuevaCompania && nuevaCompania !== policy.company;
    // Descripción del movimiento para el historial.
    let desc = `Renovada hasta ${newEnd}`;
    if (cambioCompania) desc += ` · compañía: ${policy.company || '—'} → ${nuevaCompania}`;
    if (opts.note && opts.note.trim()) desc += ` · ${opts.note.trim()}`;
    const updated = {
      ...policy,
      status: 'active',
      fromRenewalNotice: false, // ya renovada: vuelve al ciclo normal
      startDate: newStart,
      endDate: newEnd,
      ...(cambioCompania ? { company: nuevaCompania } : {}),
      history: [...(policy.history || []), { type: 'renovacion', date: fmt(new Date()), desc }],
    };
    const p = await api('PUT', `/policies/${id}`, updated);
    setPolicies(prev => prev.map(x => x.id === id ? p : x));
    showToast('Póliza renovada por 1 año');
    return p;
  };
  const deletePolicy = async (id) => {
    await api('DELETE', `/policies/${id}`);
    setPolicies(prev => prev.filter(p => p.id !== id));
    showToast('Póliza eliminada', 'warning');
  };
  const cancelPolicy = async (id, reason) => {
    const policy = policies.find(p => p.id === id);
    const updated = {
      ...policy,
      status: 'cancelled',
      cancelDate: fmt(new Date()),
      cancelReason: reason,
      history: [...(policy.history || []), { type: 'cancelacion', date: fmt(new Date()), desc: reason }],
    };
    const p = await api('PUT', `/policies/${id}`, updated);
    setPolicies(prev => prev.map(x => x.id === id ? p : x));
    showToast('Póliza cancelada', 'warning');
  };

  // COMPANIES
  const addCompany = async (data) => {
    const c = await api('POST', '/companies', data);
    setCompanies(prev => [...prev, c]);
    showToast('Compañía agregada');
  };
  const updateCompany = async (id, data) => {
    const c = await api('PUT', `/companies/${id}`, data);
    setCompanies(prev => prev.map(x => x.id === id ? c : x));
    showToast('Compañía actualizada');
  };

  // USERS
  const addUser = async (data) => {
    const u = await api('POST', '/users', data);
    setUsers(prev => [...prev, u]);
    showToast('Usuario creado');
  };
  const updateUser = async (id, data) => {
    const u = await api('PUT', `/users/${id}`, data);
    setUsers(prev => prev.map(x => x.id === id ? u : x));
    showToast('Usuario actualizado');
  };
  const deleteUser = async (id, cascade = false) => {
    const res = await api('DELETE', `/users/${id}${cascade ? '?cascade=true' : ''}`);
    if (res && res.needsConfirm) return res; // la página decide si re-pregunta
    setUsers(prev => prev.filter(u => u.id !== id));
    if (res && res.deletedEmployee) setEmployees(prev => prev.filter(e => e.userId !== id));
    showToast(res?.deletedEmployee ? 'Usuario y empleado eliminados' : 'Usuario eliminado', 'warning');
    return res;
  };

  // SINIESTROS
  const [claims, setClaims] = useState([]);
  // TICKETS
  const [tickets, setTickets] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [finances, setFinances] = useState([]);
  // CEO METRICS
  const [ceoMetrics, setCeoMetrics] = useState([]);
  // DEUDORES
  const [debts, setDebts] = useState([]);

  // Recarga todos los datos. conSpinner=false para refrescos silenciosos
  // de fondo (cada 90s y al volver a la pestaña/app): así los cambios hechos
  // desde otra sesión o dispositivo se ven sin recargar la página.
  // Guard recargandoRef: evita que dos refrescos se solapen (ej. el intervalo
  // de 90s dispara justo cuando el "volver a la pestaña" ya está cargando),
  // lo que duplicaría los 11 fetches contra el servidor.
  const recargandoRef = React.useRef(false);
  const recargarDatos = useCallback((conSpinner = true) => {
    if (!currentUser) return;
    if (recargandoRef.current) return; // ya hay una recarga en curso
    recargandoRef.current = true;
    if (conSpinner) setLoading(true);
    Promise.all([
      api('GET', '/clients').catch(() => []),
      api('GET', '/policies').catch(() => []),
      api('GET', '/companies').catch(() => []),
      api('GET', '/users').catch(() => []),
      api('GET', '/claims').catch(() => []),
      api('GET', '/tickets').catch(() => []),
      api('GET', '/ceo-metrics').catch(() => []),
      api('GET', '/debts').catch(() => []),
      api('GET', '/quotes').catch(() => []),
      api('GET', '/employees').catch(() => []),
      api('GET', '/finances').catch(() => []),
    ]).then(([c, p, co, u, cl, tk, ceo, dbt, qt, emp, fin]) => {
      setClients(Array.isArray(c) ? c : []);
      const today = new Date().toISOString().split('T')[0];
      const withExpired = (Array.isArray(p) ? p : []).map(pol => {
        // Pólizas que entraron por un AVISO DE RENOVACIÓN se muestran SIEMPRE
        // como VIGENTES mientras estén pendientes de gestión (fromRenewalNotice),
        // sin importar lo que digan sus fechas. Así no se mezclan con
        // "vencida"/"futura" y siguen apareciendo en el módulo Renovaciones.
        // (Cubre también las que quedaron mal marcadas como future/expired.)
        if (pol.fromRenewalNotice && pol.status !== 'cancelled') {
          return pol.status === 'active' ? pol : { ...pol, status: 'active' };
        }
        // Vencida: ya pasó su fecha de fin
        if ((pol.status === 'active' || pol.status === 'endorsed') && pol.endDate && pol.endDate < today) {
          return { ...pol, status: 'expired' };
        }
        // Futura: todavía no llegó su fecha de inicio (renovación ya emitida)
        if ((pol.status === 'active' || pol.status === 'endorsed') && pol.startDate && pol.startDate > today) {
          return { ...pol, status: 'future' };
        }
        // Si una póliza "futura" ya entró en vigencia, pasa a activa sola
        if (pol.status === 'future' && pol.startDate && pol.startDate <= today && (!pol.endDate || pol.endDate >= today)) {
          return { ...pol, status: 'active' };
        }
        return pol;
      });
      setPolicies(withExpired);
      setCompanies((Array.isArray(co) ? co : []).sort((a, b) => a.name.localeCompare(b.name)));
      setUsers(Array.isArray(u) ? u : []);
      setClaims(Array.isArray(cl) ? cl : []);
      setTickets(Array.isArray(tk) ? tk : []);
      setCeoMetrics(Array.isArray(ceo) ? ceo : []);
      setDebts(Array.isArray(dbt) ? dbt : []);
      setQuotes(Array.isArray(qt) ? qt : []);
      setEmployees(Array.isArray(emp) ? emp : []);
      setFinances(Array.isArray(fin) ? fin : []);
    }).catch(err => { if (conSpinner) showToast('Error cargando datos: ' + err.message, 'error'); })
      .finally(() => { recargandoRef.current = false; if (conSpinner) setLoading(false); });
  }, [currentUser, showToast]);

  // Carga inicial al loguear
  useEffect(() => { recargarDatos(true); }, [recargarDatos]);

  // Refresco silencioso: cada 90s y cada vez que la pestaña/app vuelve a
  // estar visible (clave en el iPhone, donde la app queda en segundo plano)
  useEffect(() => {
    if (!currentUser) return;
    const intervalo = setInterval(() => recargarDatos(false), 90000);
    const alVolver = () => { if (document.visibilityState === 'visible') recargarDatos(false); };
    document.addEventListener('visibilitychange', alVolver);
    return () => { clearInterval(intervalo); document.removeEventListener('visibilitychange', alVolver); };
  }, [currentUser, recargarDatos]);

  const addClaim = async (data) => {
    const c = await api('POST', '/claims', data);
    setClaims(prev => [...prev, c]);
    showToast('Siniestro creado');
    return c;
  };
  const updateClaim = async (id, data) => {
    const c = await api('PUT', `/claims/${id}`, data);
    setClaims(prev => prev.map(x => x.id === id ? c : x));
    return c;
  };
  const deleteClaim = async (id) => {
    await api('DELETE', `/claims/${id}`);
    setClaims(prev => prev.filter(c => c.id !== id));
    showToast('Siniestro eliminado', 'warning');
  };

  const addTicket = async (data) => {
    const t = await api('POST', '/tickets', data);
    setTickets(prev => [...prev, t]);
    showToast('Ticket creado');
    return t;
  };
  const updateTicket = async (id, data) => {
    const t = await api('PUT', `/tickets/${id}`, data);
    setTickets(prev => prev.map(x => x.id === id ? t : x));
    return t;
  };
  const deleteTicket = async (id) => {
    await api('DELETE', `/tickets/${id}`);
    setTickets(prev => prev.filter(t => t.id !== id));
    showToast('Ticket eliminado', 'warning');
  };

  const addQuote = async (data) => {
    const q = await api('POST', '/quotes', data);
    setQuotes(prev => [q, ...prev]);
    showToast('Cotización guardada');
    return q;
  };
  const updateQuote = async (id, data) => {
    const q = await api('PUT', `/quotes/${id}`, data);
    setQuotes(prev => prev.map(x => x.id === id ? q : x));
    return q;
  };
  const deleteQuote = async (id) => {
    await api('DELETE', `/quotes/${id}`);
    setQuotes(prev => prev.filter(q => q.id !== id));
    showToast('Cotización eliminada', 'warning');
  };

  const addEmployee = async (data) => {
    const e = await api('POST', '/employees', data);
    setEmployees(prev => [...prev, e]);
    showToast('Empleado creado');
    return e;
  };
  const updateEmployee = async (id, data) => {
    const e = await api('PUT', `/employees/${id}`, data);
    setEmployees(prev => prev.map(x => x.id === id ? e : x));
    return e;
  };
  const deleteEmployee = async (id, cascade = false) => {
    const res = await api('DELETE', `/employees/${id}${cascade ? '?cascade=true' : ''}`);
    if (res && res.needsConfirm) return res; // la página decide si re-pregunta
    setEmployees(prev => prev.filter(e => e.id !== id));
    if (res && res.deletedUser) {
      // refrescamos usuarios desde el server para reflejar el borrado en cascada
      try { setUsers(await api('GET', '/users')); } catch { /* noop */ }
    }
    showToast(res?.deletedUser ? 'Empleado y usuario eliminados' : 'Empleado eliminado', 'warning');
    return res;
  };

  const addFinance = async (data) => {
    const f = await api('POST', '/finances', data);
    setFinances(prev => [...prev, f]);
    return f;
  };
  const updateFinance = async (id, data) => {
    const f = await api('PUT', `/finances/${id}`, data);
    setFinances(prev => prev.map(x => x.id === id ? f : x));
    return f;
  };
  const deleteFinance = async (id) => {
    await api('DELETE', `/finances/${id}`);
    setFinances(prev => prev.filter(f => f.id !== id));
  };
  const deleteFinanceYear = async (year) => {
    await api('DELETE', `/finances/year/${year}`);
    setFinances(prev => prev.filter(f => String(f.year) !== String(year)));
  };
  const importFinances = async (year, records) => {
    const res = await api('POST', '/finances/import', { year, records });
    // recargar finanzas completas
    const all = await api('GET', '/finances').catch(() => []);
    setFinances(Array.isArray(all) ? all : []);
    return res;
  };

  const addCeoMetric = async (data) => {
    const m = await api('POST', '/ceo-metrics', data);
    setCeoMetrics(prev => [...prev, m]);
    showToast('Métrica guardada');
    return m;
  };
  const updateCeoMetric = async (id, data) => {
    const m = await api('PUT', `/ceo-metrics/${id}`, data);
    setCeoMetrics(prev => prev.map(x => x.id === id ? m : x));
    return m;
  };
  const deleteCeoMetric = async (id) => {
    await api('DELETE', `/ceo-metrics/${id}`);
    setCeoMetrics(prev => prev.filter(m => m.id !== id));
  };

  const addDebt = async (data) => { const d = await api('POST', '/debts', data); setDebts(prev => [...prev, d]); showToast('Mora registrada'); return d; };  const updateDebt = async (id, data) => { const d = await api('PUT', `/debts/${id}`, data); setDebts(prev => prev.map(x => x.id === id ? d : x)); return d; };
  const deleteDebt = async (id) => { await api('DELETE', `/debts/${id}`); setDebts(prev => prev.filter(d => d.id !== id)); showToast('Registro eliminado', 'warning'); };

  const value = {
    currentUser, login, logout, loading,
    users, addUser, updateUser, deleteUser,
    clients, addClient, updateClient, deleteClient,
    policies, addPolicy, updatePolicy, endorsePolicy, renewPolicy, cancelPolicy, deletePolicy,
    companies, updateCompany, addCompany,
    claims, addClaim, updateClaim, deleteClaim,
    tickets, addTicket, updateTicket, deleteTicket,
    quotes, addQuote, updateQuote, deleteQuote,
    employees, addEmployee, updateEmployee, deleteEmployee,
    finances, addFinance, updateFinance, deleteFinance, deleteFinanceYear, importFinances,
    ceoMetrics, addCeoMetric, updateCeoMetric, deleteCeoMetric,
    debts, addDebt, updateDebt, deleteDebt,
    fetchAudit: (limit = 300) => api('GET', `/audit?limit=${limit}`),
    toast, showToast,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => useContext(AppContext);
